import { Navigate, useLocation } from "react-router-dom";
import { Lock, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { FullLoader } from "../components/ProtectedRoute";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 3 14.7 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" />
  </svg>
);

export default function Login() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/dashboard" replace />;

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen cyber-hero text-white overflow-hidden" data-testid="login-page">
      <img src="/login-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale mix-blend-luminosity" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#030a1f] via-[#030a1f]/90 to-[#030a1f]/55" />
      <div className="cyber-grid absolute inset-0" />
      <div className="cyber-scan absolute inset-x-0 h-24" />
      <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-[#0ea5e9]/20 blur-3xl" />
      <div className="absolute -bottom-40 left-1/4 h-96 w-96 rounded-full bg-[#2563eb]/25 blur-3xl" />
      <div className="relative z-10 min-h-screen grid lg:grid-cols-[1.2fr_1fr]">
        <section className="flex flex-col justify-between p-6 pb-2 sm:p-12 lg:p-16">
          <div className="flex items-center gap-3 fade-up">
            <img src="/logo-siber.png" alt="Logo Siber Polri" className="h-14 w-auto drop-shadow-[0_0_16px_rgba(56,189,248,0.5)]" data-testid="login-logo" />
            <div className="leading-tight">
              <div className="font-heading font-black tracking-tight">SILOG SIBER</div>
              <div className="text-xs text-white/60">Bareskrim Polri</div>
            </div>
          </div>
          <div className="max-w-xl py-8 sm:py-16 fade-up" style={{ animationDelay: "120ms" }}>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#7dd3fc] mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#22d3ee] animate-pulse shadow-[0_0_8px_#22d3ee]" />Sistem Internal &mdash; Terbatas
            </p>
            <h1 className="font-heading font-black tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Pencatatan Logistik Subbagrenmin <span className="text-[#38bdf8] drop-shadow-[0_0_20px_rgba(56,189,248,0.45)]">Dittipidsiber</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-sm sm:text-base text-[#bfdbfe]/75 max-w-md">
              Inventaris sarpras, senjata api, almatsus Mabes, serta data almatsus Ditressiber Polda jajaran dalam satu tempat.
            </p>
          </div>
          <p className="hidden lg:block text-xs text-white/40">Direktorat Tindak Pidana Siber &middot; Hanya untuk personel berwenang</p>
        </section>
        <section className="flex items-start lg:items-center justify-center px-6 pb-10 sm:p-12">
          <div className="w-full max-w-sm border border-[#1e40af] bg-[#0b1736]/60 backdrop-blur-xl shadow-[0_0_40px_rgba(14,165,233,0.15)] p-8 fade-up" style={{ animationDelay: "220ms" }}>
            <Lock className="h-5 w-5 text-[#38bdf8]" />
            <h2 className="mt-4 font-heading text-lg font-bold">Masuk ke Sistem</h2>
            <p className="mt-1 text-sm text-white/60">Gunakan akun Google yang sudah didaftarkan oleh Admin Subbagrenmin.</p>
            {location.state?.error && (
              <div data-testid="login-error" className="mt-5 flex gap-2 border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{location.state.error}</span>
              </div>
            )}
            <Button
              data-testid="google-login-button"
              onClick={login}
              className="mt-6 w-full h-11 bg-white text-slate-900 hover:bg-white/90 active:scale-[0.98] transition-transform gap-2 font-semibold"
            >
              <GoogleIcon /> Masuk dengan Google
            </Button>
            <p className="mt-5 text-[11px] text-white/40 leading-relaxed">
              Akses dicatat. Akun Polda hanya dapat melihat dan mengisi data Polda masing-masing.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
